import { Menu02, X as CloseIcon } from '@untitledui/icons';
import { type PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  Button as AriaButton,
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';
import { useLocation } from 'react-router-dom';

import { AhoyLogo } from '@/lib/foundations/logo/ahoy-logo';
import { cx } from '@/utils/cx';

interface MobileNavigationHeaderProps extends PropsWithChildren {
  /** The org ID to link the logo to the dashboard */
  orgId?: string;
}

export const MobileNavigationHeader = ({
  children,
  orgId,
}: MobileNavigationHeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const initialKeyRef = useRef(location.key);
  const closedByNavigationRef = useRef(false);

  // Close the mobile menu when the route changes (pathname, search, or hash)
  // Using location.key which changes on any navigation action
  useEffect(() => {
    if (initialKeyRef.current !== location.key) {
      closedByNavigationRef.current = true;
      setIsOpen(false);
      initialKeyRef.current = location.key;

      // Reset the navigation flag after animations complete
      const timer = setTimeout(() => {
        closedByNavigationRef.current = false;
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.key]);

  // Handle clicks inside the menu content - set the flag synchronously
  // before navigation happens, so onOpenChange can block the reopen
  const handleMenuContentClick = () => {
    // Set flag immediately on any click inside menu content
    // This runs synchronously before onOpenChange(true) from react-aria
    closedByNavigationRef.current = true;

    // Reset the flag after a short delay if no navigation happened
    // (e.g., user clicked a non-link element)
    setTimeout(() => {
      closedByNavigationRef.current = false;
    }, 100);
  };

  // Wrap onOpenChange to prevent reopening immediately after navigation close
  const handleOpenChange = (open: boolean) => {
    if (open && closedByNavigationRef.current) {
      // Ignore attempts to reopen while we just closed due to navigation
      return;
    }
    setIsOpen(open);
  };

  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
      <header className='mobile-nav-header fixed top-0 left-0 right-0 z-30 flex h-10 items-center gap-2 border-b border-secondary bg-primary py-1.5 pl-1.5 pr-3 lg:hidden'>
        <AriaButton
          aria-label='Expand navigation menu'
          className='group flex items-center justify-center rounded-md bg-primary p-1.5 text-fg-secondary outline-focus-ring hover:bg-primary_hover hover:text-fg-secondary_hover focus-visible:outline-2 focus-visible:outline-offset-2'
        >
          <Menu02 className='size-4 transition duration-200 ease-in-out group-aria-expanded:opacity-0' />
          <CloseIcon className='absolute size-4 opacity-0 transition duration-200 ease-in-out group-aria-expanded:opacity-100' />
        </AriaButton>

        <AhoyLogo href={orgId ? `/${orgId}` : undefined} size='sm' />
      </header>

      <AriaModalOverlay
        isDismissable
        className={({ isEntering, isExiting }) =>
          cx(
            'fixed inset-0 z-50 cursor-pointer bg-overlay/70 pr-16 backdrop-blur-md lg:hidden',
            isEntering && 'duration-300 ease-in-out animate-in fade-in',
            isExiting && 'duration-200 ease-in-out animate-out fade-out'
          )
        }
      >
        <>
          <AriaButton
            aria-label='Close navigation menu'
            onPress={() => setIsOpen(false)}
            className='fixed top-3 right-2 flex cursor-pointer items-center justify-center rounded-lg p-2 text-fg-white/70 outline-focus-ring hover:bg-white/10 hover:text-fg-white focus-visible:outline-2 focus-visible:outline-offset-2'
          >
            <CloseIcon className='size-6' />
          </AriaButton>

          <AriaModal className='w-full max-w-56 cursor-auto will-change-transform'>
            <AriaDialog
              className='h-[var(--app-viewport-height,100dvh)] outline-hidden focus:outline-hidden'
              onClickCapture={handleMenuContentClick}
            >
              {children}
            </AriaDialog>
          </AriaModal>
        </>
      </AriaModalOverlay>
    </AriaDialogTrigger>
  );
};
